import { useEffect } from "react";
import { NoteRow } from "../components/NotificationBell";
import { Empty, usePageTitle } from "../components/ui";
import { type Note, useNotifications } from "../notify";

/**
 * Everything the app has said in the last thirty days, newest first, grouped by day (Tyler,
 * 2026-09-24). The bell shows the last ten and links here for the rest. An action is still pressable
 * from here — an undo on a receipt from last week works as it did the moment it was received — until
 * it has been used, on this device or another.
 */
export function NotificationsPage() {
  usePageTitle("Notifications");
  const { notes, act, runners, markAllRead, unread } = useNotifications();

  // Looking at them all is reading them all, as opening the bell is.
  useEffect(() => {
    if (unread > 0) markAllRead();
  }, [unread, markAllRead]);

  const days = groupByDay(notes);

  return (
    <>
      <header className="page-head">
        <div className="page-head-title">
          <h1>Notifications</h1>
        </div>
      </header>
      <div className="page-body stack">
        {notes.length === 0 ? (
          <Empty title="Nothing yet">
            <p className="muted">
              What the app has to say turns up here, and stays for thirty days.
            </p>
          </Empty>
        ) : (
          days.map(([day, list]) => (
            <section className="section" key={day}>
              <div className="section-header" style={{ cursor: "default" }}>
                <h2>{day}</h2>
                <span className="aside">{list.length}</span>
              </div>
              <div className="section-body notifications-list">
                {list.map((note) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    runner={note.action ? runners[note.action.data.kind] : undefined}
                    onAct={() => act(note.id)}
                    compact={false}
                  />
                ))}
              </div>
            </section>
          ))
        )}
        <p className="muted notifications-kept">Notifications are kept for thirty days.</p>
      </div>
    </>
  );
}

function groupByDay(notes: Note[]): [string, Note[]][] {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  const label = (at: number) => {
    const d = new Date(at);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  };
  const days = new Map<string, Note[]>();
  for (const note of [...notes].sort((a, b) => b.at - a.at)) {
    const key = label(note.at);
    days.set(key, [...(days.get(key) ?? []), note]);
  }
  return [...days];
}
